import { EMPTY, BLACK, WHITE, DIRECTIONS } from './gameConstants.js'
import { toCoord, isValidCoord, toIndex } from './boardUtils.js'

/**
 * 计算在指定位置落子后可以翻转的对手棋子
 * @param {number[]} board - 棋盘数组
 * @param {number} index - 落子位置索引
 * @param {number} player - 当前玩家 (BLACK 或 WHITE)
 * @returns {number[]} 可以翻转的棋子索引数组
 */
export function getFlips(board, index, player) {
  if (board[index] !== EMPTY) return []

  const opponent = player === BLACK ? WHITE : BLACK
  const [row, col] = toCoord(index)
  const flips = []

  // 检查所有8个方向
  for (const [dr, dc] of DIRECTIONS) {
    const line = []
    let nr = row + dr
    let nc = col + dc

    // 沿着方向收集对手棋子
    while (isValidCoord(nr, nc) && board[toIndex(nr, nc)] === opponent) {
      line.push(toIndex(nr, nc))
      nr += dr
      nc += dc
    }

    // 如果末端是自己的棋子，则这条线上的对手棋子都可以翻转
    if (line.length > 0 &&
        isValidCoord(nr, nc) &&
        board[toIndex(nr, nc)] === player) {
      flips.push(...line)
    }
  }

  return flips
}

/**
 * 检查在指定位置落子是否合法
 * @param {number[]} board - 棋盘数组
 * @param {number} index - 落子位置索引
 * @param {number} player - 当前玩家
 * @returns {boolean} 是否合法
 */
export function isValidMove(board, index, player) {
  return getFlips(board, index, player).length > 0
}

/**
 * 获取指定玩家的所有合法落子位置
 * @param {number[]} board - 棋盘数组
 * @param {number} player - 当前玩家
 * @returns {number[]} 合法落子位置索引数组
 */
export function getValidMoves(board, player) {
  const moves = []
  for (let i = 0; i < board.length; i++) {
    if (isValidMove(board, i, player)) {
      moves.push(i)
    }
  }
  return moves
}

/**
 * 执行落子并翻转棋子
 * @param {number[]} board - 棋盘数组
 * @param {number} index - 落子位置索引
 * @param {number} player - 当前玩家
 * @returns {number[]} 新的棋盘数组，如果移动非法则返回原数组
 */
export function applyMove(board, index, player) {
  const details = applyMoveWithDetails(board, index, player)
  if (!details) return board
  return details.board
}

/**
 * 执行落子并返回翻转列表（用于记谱与 UI 标注）
 * @returns {{ board: number[], flips: number[] } | null}
 */
export function applyMoveWithDetails(board, index, player) {
  const flips = getFlips(board, index, player)

  if (flips.length === 0) {
    return null
  }

  const newBoard = [...board]
  newBoard[index] = player
  flips.forEach(flipIndex => {
    newBoard[flipIndex] = player
  })

  return { board: newBoard, flips }
}

/**
 * 计算分数
 * @param {number[]} board - 棋盘数组
 * @returns {{black: number, white: number}} 黑白双方分数
 */
export function calculateScore(board) {
  let black = 0
  let white = 0

  board.forEach(cell => {
    if (cell === BLACK) black++
    else if (cell === WHITE) white++
  })

  return { black, white }
}

/**
 * 检查游戏是否结束
 * @param {number[]} board - 棋盘数组
 * @returns {boolean} 游戏是否结束
 */
export function isGameOver(board) {
  const blackMoves = getValidMoves(board, BLACK)
  const whiteMoves = getValidMoves(board, WHITE)

  return blackMoves.length === 0 && whiteMoves.length === 0
}

/**
 * 判断游戏结果
 * @param {number[]} board - 棋盘数组
 * @returns {string} 'black' | 'white' | 'draw'
 */
export function getWinner(board) {
  const { black, white } = calculateScore(board)

  if (black > white) return 'black'
  if (white > black) return 'white'
  return 'draw'
}